import type { ReactElement } from 'react'
import { useId } from 'react'

import type { DisplayRect } from './liveValueDisplayRect'

const LIVE_GLOW_COLOR = '#22C55E'
const STALE_GLOW_COLOR = '#F59E0B'
const GLOW_MARGIN_SCREEN_PX = 2
const GLOW_BASE_CORNER_RADIUS = 2
const GLOW_OPACITY = 0.4
const GLOW_BLUR_SCREEN_PX = 2
const GLOW_MAX_CHIP_HEIGHT_FRACTION = 0.4

export function LiveValueGlowLayer({
  chips,
  glowPxPerImagePx = 1,
}: {
  chips: (DisplayRect & { key: string; stale: boolean })[]
  glowPxPerImagePx?: number
}): ReactElement {
  const glowFilterId = useId()

  const smallestChipHeight =
    chips.length === 0 ? 0 : Math.min(...chips.map((chip) => chip.height))
  const maxGlowExtent = smallestChipHeight * GLOW_MAX_CHIP_HEIGHT_FRACTION
  const glowMargin = Math.min(
    GLOW_MARGIN_SCREEN_PX / glowPxPerImagePx,
    maxGlowExtent
  )
  const glowBlur = Math.min(
    GLOW_BLUR_SCREEN_PX / glowPxPerImagePx,
    maxGlowExtent
  )
  const glowCornerRadius = GLOW_BASE_CORNER_RADIUS + glowMargin

  return (
    <>
      <defs>
        <filter
          id={glowFilterId}
          x='-100%'
          y='-100%'
          width='300%'
          height='300%'>
          <feGaussianBlur stdDeviation={glowBlur} />
        </filter>
      </defs>
      {chips.map((chip) => (
        <rect
          key={chip.key}
          data-live-chip-glow={chip.stale ? 'stale' : 'live'}
          x={chip.x - glowMargin}
          y={chip.y - glowMargin}
          width={chip.width + glowMargin * 2}
          height={chip.height + glowMargin * 2}
          rx={glowCornerRadius}
          ry={glowCornerRadius}
          fill={chip.stale ? STALE_GLOW_COLOR : LIVE_GLOW_COLOR}
          opacity={GLOW_OPACITY}
          filter={`url(#${glowFilterId})`}
          pointerEvents='none'
        />
      ))}
    </>
  )
}
